import { i18n } from '@/common/locale';
import { getUtcOffset, getUtcOffsetByTimezone } from '@/common/utils/commonUtils';
import type { QueryStatTimezoneOption } from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/common/queryStatTimezoneOption.setup';
import dayjs, { extend } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

extend(utc);
extend(timezone);

export interface QueryStatTooltipBadge {
  label: string;
  helpText?: string;
}

interface GetQueryStatTooltipTextOptions {
  noneLabelKey?: string;
}

const QUERY_STAT_TIME_FIELDS = [
  'fromTime',
  'toTime',
  'additionalFromTime',
  'additionalToTime',
  'lastTime',
] as const;

type QueryStatTimeField = (typeof QUERY_STAT_TIME_FIELDS)[number];

export const getQueryStatTimezoneUtcOffset = (
  queryStatTimezone?: QueryStatTimezoneOption,
): number => {
  const mode = queryStatTimezone?.mode ?? 'account';

  if (mode === 'none') {
    return 0;
  }

  if (mode === 'manual') {
    return queryStatTimezone?.timezone
      ? getUtcOffsetByTimezone(queryStatTimezone.timezone)
      : getUtcOffset();
  }

  return getUtcOffset();
};

export const getQueryStatTimezoneRequestShiftMinutes = (
  queryStatTimezone?: QueryStatTimezoneOption,
  accountUtcOffset: number = getUtcOffset(),
): number => {
  return accountUtcOffset - getQueryStatTimezoneUtcOffset(queryStatTimezone);
};

const shiftUtcTimeString = (time: unknown, shiftMinutes: number): unknown => {
  if (typeof time !== 'string' || !time || shiftMinutes === 0) {
    return time;
  }

  const shifted = dayjs.utc(time).add(shiftMinutes, 'minute');

  if (!shifted.isValid()) {
    return time;
  }

  return shifted.format('YYYY-MM-DD HH:mm:ss');
};

/**
 * UTC string 을 queryStatTimezone 옵션에 맞춰 표시용 시각 string 으로 변환한다.
 *
 * worker 의 `utcEpochToStandardEpoch` 와 의미적 동치. worker 는 epoch 출력, 본 헬퍼는 clock string
 * 출력이 다를 뿐 동일 timezone 좌표계를 만든다. axesX range 같은 frontend 표시값 변환에서
 * `utcZeroTimeToStandardTime` (항상 계정 TZ) 대신 사용해 worker label epoch 와 좌표계를 맞춘다.
 *
 * - account: 계정 timezone 으로 변환
 * - manual + timezone: 해당 timezone 으로 변환 (DST 인지)
 * - none / fallback: utcOffset shift (`getQueryStatTimezoneUtcOffset` 기반)
 */
export const convertUtcStringToQueryStatStandardTime = (
  utcString: string | null | undefined,
  queryStatTimezone: QueryStatTimezoneOption | undefined,
  accountTimezone: string | undefined,
  returnFormat = 'YYYY-MM-DD HH:mm:ss',
): string => {
  if (!utcString) return '';

  const mode = queryStatTimezone?.mode ?? 'account';
  let tz: string | undefined;
  if (mode === 'account') tz = accountTimezone;
  else if (mode === 'manual') tz = queryStatTimezone?.timezone;

  if (tz) {
    return dayjs.utc(utcString).tz(tz).format(returnFormat);
  }

  // none 또는 manual without timezone: utcOffset shift 경로.
  // 'none' 모드에서 `getQueryStatTimezoneUtcOffset` 는 항상 0 이므로 본 호출은 UTC clock 그대로
  // 반환하고, 차트 측에서 같은 좌표계로 라벨 epoch 가 들어오기만 하면 정합. (worker
  // `utcEpochToStandardEpoch` 의 utcOffset=0 분기와 동치.)
  const utcOffsetMinutes = getQueryStatTimezoneUtcOffset(queryStatTimezone);
  return dayjs.utc(utcString).add(utcOffsetMinutes, 'minute').format(returnFormat);
};

export const applyQueryStatTimezoneToRequest = <T extends Record<string, any>>(
  requestParam: T,
  queryStatTimezone?: QueryStatTimezoneOption,
  accountUtcOffset: number = getUtcOffset(),
): T => {
  const shiftMinutes = getQueryStatTimezoneRequestShiftMinutes(queryStatTimezone, accountUtcOffset);

  if (shiftMinutes === 0) {
    return requestParam;
  }

  const transformedRequest = { ...requestParam } as T & Record<QueryStatTimeField, unknown>;
  let isChanged = false;

  QUERY_STAT_TIME_FIELDS.forEach((field) => {
    const shiftedTime = shiftUtcTimeString(requestParam[field], shiftMinutes);

    if (shiftedTime !== requestParam[field]) {
      transformedRequest[field] = shiftedTime;
      isChanged = true;
    }
  });

  return isChanged ? transformedRequest : requestParam;
};

export const formatUtcOffsetLabel = (utcOffset: number): string => {
  const sign = utcOffset >= 0 ? '+' : '-';
  const absoluteUtcOffset = Math.abs(utcOffset);
  const hours = String(Math.floor(absoluteUtcOffset / 60)).padStart(2, '0');
  const minutes = String(absoluteUtcOffset % 60).padStart(2, '0');

  return `UTC${sign}${hours}:${minutes}`;
};

export const getQueryStatTooltipText = (
  queryStatTimezone?: QueryStatTimezoneOption,
  options?: GetQueryStatTooltipTextOptions,
): QueryStatTooltipBadge | null => {
  const mode = queryStatTimezone?.mode ?? 'account';

  if (mode === 'account') {
    return null;
  }

  if (mode === 'none') {
    return {
      label: i18n.global.t(options?.noneLabelKey ?? 'WORD.UI.DASHBOARD.QUERY_STAT_QUERY_TIME'),
      helpText: i18n.global.t('DESC.UI.QUERY_STAT_QUERY_TIME_BADGE_TOOLTIP'),
    };
  }

  if (mode === 'manual' && queryStatTimezone?.timezone) {
    return {
      label: formatUtcOffsetLabel(getQueryStatTimezoneUtcOffset(queryStatTimezone)),
    };
  }

  return null;
};
